// src/services/authService.js
import { auth, db } from "../firebase/config";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";

/* =========================
   INSCRIPTION
========================= */
export const registerUser = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Création du profil utilisateur Firestore avec subscriptionType
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      email: user.email,
      role: "user",
      subscription: "free",
      subscriptionType: "free",
      diagnosticsLimit: 1,
      diagnosticsThisMonth: 0,
      createdAt: serverTimestamp(),
    });

    return { success: true, user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/* =========================
   CONNEXION
========================= */
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/* =========================
   DÉCONNEXION
========================= */
export const logoutUser = async () => {
  await signOut(auth);
};

/* =========================
   ÉCOUTE AUTH
========================= */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/* =========================
   DONNÉES UTILISATEUR
   ✅ Avec option forceRefresh
========================= */
export const getUserData = async (uid, options = {}) => {
  try {
    const ref = doc(db, "users", uid);
    
    // ✅ Force le rechargement depuis Firestore (pas de cache)
    // Note: getDoc() force toujours le refresh par défaut avec Firestore
    // Mais on garde l'option pour la flexibilité future
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      return { success: false, error: "Utilisateur introuvable" };
    }

    const data = snap.data();
    
    console.log('📊 Données utilisateur rechargées:', {
      uid,
      subscriptionType: data.subscriptionType,
      diagnosticsLimit: data.diagnosticsLimit,
      stripeCustomerId: data.stripeCustomerId ? '✅ Présent' : '❌ Absent',
      forceRefresh: options.forceRefresh
    });

    return { success: true, data };
  } catch (error) {
    console.error('❌ Erreur getUserData:', error);
    return { success: false, error: error.message };
  }
};

/* =========================
   RÉINITIALISATION MOT DE PASSE
========================= */

/**
 * Envoie un email de réinitialisation de mot de passe
 * @param {string} email - L'adresse email de l'utilisateur
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendPasswordResetEmail = async (email) => {
  try {
    await firebaseSendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/login`,
      handleCodeInApp: false,
    });
    return { success: true };
  } catch (error) {
    console.error("Erreur lors de l'envoi de l'email:", error);
    
    // Messages d'erreur personnalisés
    let errorMessage = error.message;
    if (error.code === "auth/user-not-found") {
      errorMessage = "Aucun compte n'existe avec cet email.";
    } else if (error.code === "auth/invalid-email") {
      errorMessage = "L'adresse email n'est pas valide.";
    } else if (error.code === "auth/too-many-requests") {
      errorMessage = "Trop de tentatives. Veuillez réessayer plus tard.";
    }
    
    return { success: false, error: errorMessage };
  }
};

/**
 * Vérifie la validité d'un token de réinitialisation
 * @param {string} token - Le token de réinitialisation (oobCode)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const verifyResetToken = async (token) => {
  try {
    await verifyPasswordResetCode(auth, token);
    return { success: true };
  } catch (error) {
    console.error("Erreur de vérification du token:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Réinitialise le mot de passe avec un nouveau mot de passe
 * @param {string} token - Le token de réinitialisation (oobCode)
 * @param {string} newPassword - Le nouveau mot de passe
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const resetPassword = async (token, newPassword) => {
  try {
    await confirmPasswordReset(auth, token, newPassword);
    return { success: true };
  } catch (error) {
    console.error("Erreur lors de la réinitialisation:", error);
    
    // Messages d'erreur personnalisés
    let errorMessage = error.message;
    if (error.code === "auth/invalid-action-code") {
      errorMessage = "Le lien de réinitialisation a expiré ou est invalide.";
    } else if (error.code === "auth/expired-action-code") {
      errorMessage = "Le lien de réinitialisation a expiré. Veuillez en demander un nouveau.";
    } else if (error.code === "auth/weak-password") {
      errorMessage = "Le mot de passe est trop faible.";
    }
    
    return { success: false, error: errorMessage };
  }
};